import React from 'react';

import { observer } from 'mobx-react';

import classNames from 'classnames';
import classes from './LeaderboardRowDesktop.module.scss';
import { LeaderboardRowInfo } from '../../../../../interfaces/leaderboard/LeaderboardRowInfo';
import {
  LEADERBOARD_COLUMN,
  LeaderboardColumn,
  RenderColumnInfo,
} from '../../../ChallengeLeaderboard.settings';
import {
  GridTableCell,
  GridTableRow,
  GridTableRowVariant,
} from '../../../../../../shared/components/table-components';
import { RankCell } from '../RankCell/RankCell';
import { InitialScanIndicator } from '../InitialScanIndicatorCell/InitialScanIndicator';
import { CustomerCell } from '../CustomerCell/CustomerCell';
import { MetricTrendValue } from '../MetricTrendValue/MetricTrendValue';
import { leaderboardStore } from '../../../../../stores/leaderboardStore';
import { getValueOrDash } from '../../../../../../shared/utils/stringUtils/getValueOrDash/getValueOrDash';
import { MuscleMultiplierAdditionalInfo } from '../MuscleMultiplierAdditionalInfo/MuscleMultiplierAdditionalInfo';
import { getFormattedDate } from '../../../../../../shared/utils/dateUtils';
import {
  dateTimeFormat,
  localDateTimeFormat,
} from '../../../../../../shared/constants/apiDateFormat';

type LeaderboardRowDesktopProps = {
  leaderboardRow: LeaderboardRowInfo;
  leaderboardColumns: LeaderboardColumn[];
  variant?: GridTableRowVariant;
};

const LeaderboardRowDesktop = observer(
  ({
    leaderboardRow,
    leaderboardColumns,
    variant = 'compactRow',
  }: LeaderboardRowDesktopProps) => {
    const { challengeInfo } = leaderboardStore;
    const { scoringRules } = challengeInfo || {};

    const rowClassName = classNames(classes.row, {
      [classes.inBox]: variant === 'unstyled',
    });

    const renderColumnsInfo: RenderColumnInfo = {
      [LEADERBOARD_COLUMN.RANK]: <RankCell rank={leaderboardRow.rank} />,
      [LEADERBOARD_COLUMN.INITIAL_SCAN_INDICATOR]: (
        <GridTableCell justify="center" align="center">
          <InitialScanIndicator isCompleted={Boolean(leaderboardRow.initialScanDate)} />
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.CUSTOMER]: (
        <CustomerCell
          nickname={leaderboardRow.nickname}
          gender={leaderboardRow.gender}
          age={leaderboardRow.age}
          tooltip={
            leaderboardRow.initialScanDate ? (
              <span>
                Initial Scan Completed:
                <br />
                {getFormattedDate(
                  leaderboardRow.initialScanDate,
                  localDateTimeFormat,
                  dateTimeFormat,
                )}
              </span>
            ) : (
              <span>Initial Scan Not Completed</span>
            )
          }
        />
      ),
      [LEADERBOARD_COLUMN.TOTAL_SCORE]: (
        <GridTableCell>{getValueOrDash(leaderboardRow.totalScore)}</GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FOOD_TRACKING_PERCENT]: (
        <GridTableCell>
          {getValueOrDash(leaderboardRow.foodTrackingPercentage, '%')}
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FOOD_TRACKING_RANK]: (
        <GridTableCell>{getValueOrDash(leaderboardRow.foodTrackingRank)}</GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_PERCENT]: (
        <GridTableCell>
          <MetricTrendValue
            value={leaderboardRow.fatLossPercentage}
            arrowOnPositive="down"
            unit="%"
          />
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.ADJUSTED_FAT_LOSS_PERCENT]: (
        <GridTableCell>
          <MetricTrendValue
            value={leaderboardRow.adjustedFatLossPercentage}
            arrowOnPositive="down"
            unit="%"
          />
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_RANK]: (
        <GridTableCell>{getValueOrDash(leaderboardRow.fatLossRank)}</GridTableCell>
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_LBS]: (
        <GridTableCell>
          <MetricTrendValue value={leaderboardRow.fatLossLbs} arrowOnPositive="down" />
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.MUSCLE_GAIN_LBS]: (
        <GridTableCell>
          <>
            <MetricTrendValue value={leaderboardRow.muscleGainLbs} arrowOnPositive="up" />
            &nbsp;
            <MuscleMultiplierAdditionalInfo
              scoringRuleType={scoringRules}
              muscleGainLbs={leaderboardRow.muscleGainLbs}
              muscleGainMultiplier={leaderboardRow.muscleGainMultiplier}
            />
          </>
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.MUSCLE_MULTIPLIER]: (
        <GridTableCell>
          {getValueOrDash(leaderboardRow.muscleGainMultiplier, '*', 'prefix')}
        </GridTableCell>
      ),
      [LEADERBOARD_COLUMN.ADJUSTED_SCORE_PERCENT]: (
        <GridTableCell>
          <MetricTrendValue
            value={leaderboardRow.adjustedFatLossPercentage}
            arrowOnPositive="down"
            unit="%"
          />
        </GridTableCell>
      ),
    };

    return (
      <GridTableRow className={rowClassName} variant={variant}>
        {leaderboardColumns.map((column) => (
          <React.Fragment key={column}>{renderColumnsInfo[column]}</React.Fragment>
        ))}
      </GridTableRow>
    );
  },
);

export { LeaderboardRowDesktop };
